import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle, faRefresh } from '@fortawesome/free-solid-svg-icons';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error for debugging
    console.error('🚨 [ERROR BOUNDARY] Caught an error:', error);
    console.error('🚨 [ERROR BOUNDARY] Error info:', errorInfo);
    
    // Check if this is the specific "Objects are not valid as a React child" error
    if (error.message.includes('Objects are not valid as a React child')) {
      console.error('🚨 [ERROR BOUNDARY] Detected object rendering error - likely a Prisma error object being rendered directly');
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <Alert variant="danger" className="m-3">
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <FontAwesomeIcon icon={faExclamationTriangle} className="me-2" />
              <strong>Something went wrong</strong>
            </div>
            <Button variant="outline-danger" size="sm" onClick={this.handleRetry}>
              <FontAwesomeIcon icon={faRefresh} className="me-1" />
              Try Again
            </Button>
          </div>
          <div className="mt-2">
            <small className="text-muted">
              An error occurred while rendering this component. Please try refreshing the page.
            </small>
          </div>
          {process.env.NODE_ENV === 'development' && this.state.error && (
            <details className="mt-2">
              <summary className="text-muted" style={{ cursor: 'pointer' }}>
                <small>Error Details (Development Only)</small>
              </summary>
              <pre className="mt-2 text-danger" style={{ fontSize: '0.75rem', whiteSpace: 'pre-wrap' }}>
                {this.state.error.message}
              </pre>
            </details>
          )}
        </Alert>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 